import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "../../layout/Layout";
import PageBanner from "../../layout/PageBanner";
import { authService, cartService, catalogService, paymentService, ORDER_TYPE } from "../../services";
import { Panel, PanelHead, PayLogo, PhoneField, Qty, Skeleton, TextField, isValidPhone, money } from "../../components/ui";

// หน้าสั่งซื้อรวมของ เติมเกม / เติมเงินมือถือ / บัตรเติมเงิน
// kind: "game" | "mobile" | "card"
const CONFIG = {
  game: {
    label: "เติมเกม",
    listPath: "/topup/game",
    fetch: (id) => catalogService.game(id),
    orderType: ORDER_TYPE.game,
    historyPath: "/profile/topup-game-history",
    packageTitle: "คลิกเลือกจำนวนเงินที่ต้องการ",
  },
  mobile: {
    label: "เติมเงินมือถือ",
    listPath: "/topup/mobile",
    fetch: (id) => catalogService.mobileOperator(id),
    orderType: ORDER_TYPE.mobile,
    historyPath: "/profile/topup-mobile-history",
    packageTitle: "คลิกเลือกจำนวนเงินที่ต้องการ",
  },
  card: {
    label: "บัตรเติมเงิน",
    listPath: "/topup/card",
    fetch: (id) => catalogService.card(id),
    orderType: ORDER_TYPE.card,
    historyPath: "/profile/topup-card-history",
    packageTitle: "คลิกเลือกมูลค่าบัตรที่ต้องการ",
  },
};

export default function TopupDetail({ kind = "game" }) {
  const config = CONFIG[kind];
  const { id } = useParams();
  const navigate = useNavigate();

  const [item, setItem] = useState(null);
  const [channels, setChannels] = useState([]);
  const [selected, setSelected] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [inputs, setInputs] = useState({});
  const [payment, setPayment] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [added, setAdded] = useState(false);

  useEffect(() => {
    let alive = true;
    setItem(null);
    setAdded(false);
    setSelected(null);
    config.fetch(id).then((data) => alive && setItem(data));
    catalogService.orderPaymentChannels().then((rows) => {
      if (!alive) return;
      setChannels(rows || []);
      setPayment(rows?.[0]?.id || null);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, kind]);

  const subtotal = useMemo(() => (selected ? selected.price * quantity : 0), [selected, quantity]);
  const channel = channels.find((c) => c.id === payment);
  const fee = channel ? Math.round(subtotal * channel.feeRate * 100) / 100 : 0;
  const total = Math.max(0, subtotal + fee);

  const inputsValid = (item?.inputs || []).every((field) => {
    const value = (inputs[field.key] || "").trim();
    if (!value) return false;
    if (field.key === "phone" || field.type === "tel") return isValidPhone(value);
    return true;
  });
  const canSubmit = selected && accepted && payment && inputsValid;
  // เกมแบบ ID/Password มีรหัสผ่านลูกค้า ไม่เก็บลง localStorage -> ไม่ให้ใส่ตะกร้า (จ่ายทันทีได้ตามเดิม)
  const hasSecretInput = (item?.inputs || []).some((field) => field.type === "password");
  const canAddToCart = selected && inputsValid && !hasSecretInput;

  const addToCart = () => {
    cartService.add({ kind, itemId: item.id, packageId: selected.id, inputs, quantity });
    setAdded(true);
  };

  const submit = async () => {
    if (!authService.isLoggedIn()) {
      navigate("/login", { state: { from: window.location.pathname } });
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const result = await paymentService.createQrOrder({
        amount: total,
        orderType: config.orderType,
        remark: `${config.label} ${item.name}`,
        items: [
          {
            productName: `${item.name} - ${selected.name}`,
            productCode: String(selected.id),
            productImage: item.image,
            quantity,
            price: selected.price,
            orderType: config.orderType,
            // รายละเอียดเฉพาะของ LucidTopup เก็บไว้ใน remark ของ order_item
            detail: {
              kind,
              itemId: item.id,
              itemName: item.name,
              packageId: selected.id,
              packageName: selected.name,
              inputs,
              fee,
            },
          },
        ],
      });

      navigate(`/payment/qr/${result.transactionId}`, {
        state: {
          base64: result.base64,
          amount: result.amount,
          refNo: result.refNo,
          expireDatetime: result.expireDatetime,
          title: `${config.label} ${item.name}`,
          detail: `${selected.name} x ${quantity}`,
          historyPath: config.historyPath,
        },
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!item) {
    return (
      <Layout>
        <PageBanner title={config.label} breadcrumb={[{ label: config.label, to: config.listPath }]} seed={3} />
        <div className="lucid-container" style={{ marginTop: -30, paddingBottom: 40 }}>
          <Skeleton height={420} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageBanner
        title={`${config.label} ${item.name}`}
        breadcrumb={[{ label: config.label, to: config.listPath }, { label: item.name }]}
        seed={item.id % 20}
      />

      <div className="lucid-container" style={{ marginTop: -30, position: "relative", zIndex: 3, paddingBottom: 40 }}>
        <div className="lt-order-layout">
          <aside className="lt-order-side">
            <img className="lt-order-side__image" src={item.image} alt={item.name} />
            <div className="lt-order-side__body">
              <h3>{item.name}</h3>
              <p>{item.note}</p>
              <Link to={config.historyPath} className="lt-btn lt-btn--navy lt-btn--block lt-btn--pill">
                ประวัติคำสั่งซื้อ
              </Link>
            </div>
          </aside>

          <div>
            <Panel>
              <PanelHead step="1" title={config.packageTitle} />
              <p className="lt-helper lt-mb-16">เลือกแพ็กเกจ</p>
              <div className="lt-package-grid">
                {item.packages.map((pack) => (
                  <button
                    type="button"
                    key={pack.id}
                    className={`lt-package ${selected?.id === pack.id ? "is-active" : ""}`}
                    onClick={() => setSelected(pack)}
                  >
                    <div className="lt-package__icon">{kind === "mobile" ? "฿" : "★"}</div>
                    <div className="lt-package__name">{pack.name}</div>
                    <div className="lt-package__price">
                      ฿ {money(pack.price, 0)}
                      {pack.oldPrice ? <span className="lt-package__old">฿ {money(pack.oldPrice, 0)}</span> : null}
                    </div>
                    {pack.bonus ? <div className="lt-package__bonus">{pack.bonus}</div> : null}
                  </button>
                ))}
              </div>

              <div className="lt-flex lt-gap-12 lt-mt-24" style={{ alignItems: "center" }}>
                <span>จำนวน</span>
                <Qty value={quantity} onChange={setQuantity} />
              </div>
            </Panel>

            {item.inputs?.length ? (
              <Panel>
                <PanelHead step="2" title="ใส่ข้อมูลของลูกค้า" action={<span className="lt-helper">[ วิธีดูข้อมูล ]</span>} />
                {item.inputs.map((field) => {
                  const commonProps = {
                    label: field.label,
                    value: inputs[field.key] || "",
                    onChange: (event) => setInputs((prev) => ({ ...prev, [field.key]: event.target.value })),
                  };
                  // ช่องเบอร์โทรศัพท์บังคับตัวเลข 10 หลัก
                  return field.key === "phone" || field.type === "tel" ? (
                    <PhoneField key={field.key} placeholder={field.placeholder} {...commonProps} />
                  ) : (
                    <TextField key={field.key} type={field.type || "text"} placeholder={field.placeholder} {...commonProps} />
                  );
                })}
              </Panel>
            ) : null}

            <Panel>
              <PanelHead step={item.inputs?.length ? "3" : "2"} title="ช่องทางชำระเงิน" />
              <div className="lt-paymethod-grid">
                {channels.map((option) => (
                  <button
                    type="button"
                    key={option.id}
                    className={`lt-paymethod ${payment === option.id ? "is-active" : ""}`}
                    onClick={() => setPayment(option.id)}
                  >
                    <span className="lt-paymethod__check">✓</span>
                    <PayLogo text={option.logoText} sub={option.logoSub} />
                    <div className="lt-paymethod__body">
                      <div className="lt-paymethod__name">{option.name}</div>
                      <span className={`lt-tag lt-tag--${option.tone}`}>{option.badge}</span>
                    </div>
                  </button>
                ))}
              </div>

              <div className="lt-summary">
                <div className="lt-summary__row">
                  <span>ยอดรวม</span>
                  <span>฿ {money(subtotal)}</span>
                </div>
                <div className="lt-summary__row">
                  <span>ค่าธรรมเนียม</span>
                  <span>฿ {money(fee)}</span>
                </div>
                <div className="lt-summary__row lt-summary__row--total">
                  <span>ราคาสุทธิ</span>
                  <span>฿ {money(total)}</span>
                </div>
              </div>

              <label className="lt-check lt-mt-24">
                <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
                <span>
                  ฉันยืนยันทำรายการ ยอมรับ <Link to="/policy">ข้อกำหนด</Link> และรับทราบว่าสินค้าไม่สามารถคืนเงินได้ทุกกรณี
                </span>
              </label>

              {error ? <p className="lt-helper lt-helper--danger lt-mt-24">{error}</p> : null}

              <button
                type="button"
                className={`lt-btn lt-btn--teal lt-btn--block lt-btn--cut lt-mt-24 ${canSubmit ? "" : "is-disabled"}`}
                disabled={!canSubmit || submitting}
                onClick={submit}
              >
                {submitting ? "กำลังสร้าง QR..." : "ยืนยันการเติม"}
              </button>

              <button
                type="button"
                className={`lt-btn lt-btn--ghost lt-btn--block lt-mt-16 ${canAddToCart ? "" : "is-disabled"}`}
                disabled={!canAddToCart}
                onClick={addToCart}
              >
                🛒 ใส่ตะกร้า
              </button>
              {hasSecretInput ? (
                <p className="lt-helper lt-mt-16">รายการที่ต้องกรอกรหัสผ่านไม่สามารถใส่ตะกร้าได้ เพื่อความปลอดภัย กรุณากดยืนยันการเติมเพื่อจ่ายทันที</p>
              ) : !canAddToCart ? (
                <p className="lt-helper lt-mt-16">เลือกแพ็กเกจและกรอกข้อมูลลูกค้าให้ครบก่อนใส่ตะกร้า</p>
              ) : null}
              {added ? (
                <p className="lt-helper lt-mt-16" role="status">
                  เพิ่มลงตะกร้าแล้ว · <Link to="/cart">ไปที่ตะกร้า</Link>
                </p>
              ) : null}
            </Panel>
          </div>
        </div>
      </div>
    </Layout>
  );
}
